import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TypedFormBuilder } from 'ZalamoLibrary/src/lib/ui/form/TypedFormBuilder';
import {
  BehaviorSubject,
  distinctUntilChanged,
  first,
  map,
  pluck,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';
import { Validators } from '@angular/forms';
import 'quill-mention/autoregister';
import { GetMembersGQL } from 'ZalamoLibrary/src/lib/ui/organization-member/graphql';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';

import { translationProvider } from '@ui/nls/translations/translate.directive';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { hasMeaningfulCommentDraft } from './comment-draft.helpers';

@Component({
  selector: 'lib-component-form-field',
  templateUrl: './component-form-field.component.html',
  styleUrls: ['./component-form-field.component.scss'],
  providers: [translationProvider('orderManagement')],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ComponentFormFieldComponent extends AliveState implements OnInit {
  @ViewChild(QuillEditorComponent, {
    static: false,
  })
  quill: QuillEditorComponent;
  formVisible$ = new BehaviorSubject(false);
  model = this.form.typedGroup<{ comment: string }>({
    comment: ['', Validators.required],
  });
  @Output() addComment = new EventEmitter<string>();
  @Output() draftDirtyChange = new EventEmitter<boolean>();
  editor: Quill;
  modules = {
    mention: this.setupMention(),
    keyboard: {
      bindings: {
        shift_enter: {
          key: 13,
          shiftKey: true,
          handler: (range, ctx) => {
            console.log(range, ctx); // if you want to see the output of the binding
            this.editor.insertText(range.index, '\n');
          },
        },
        enter: {
          key: 13,
          handler: () => {
            this.submitComment();
          },
        },
      },
    },
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'], // toggled buttons
      ['blockquote', 'code-block'],

      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
      [{ direction: 'rtl' }], // text direction

      [{ header: [1, 2, 3, 4, 5, 6, false] }],

      ['clean'], // remove formatting button

      // ['link', 'image', 'video'], // link and image, video
    ],
  };

  constructor(
    private form: TypedFormBuilder,
    private getMembersGQL: GetMembersGQL,
    private organizationService: OrganizationService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subscribeWhileAlive(
      this.model.controls.comment.valueChanges.pipe(
        startWith(this.model.value.comment),
        map((comment) => hasMeaningfulCommentDraft(comment)),
        distinctUntilChanged(),
        tap((hasDraft) => this.draftDirtyChange.emit(hasDraft)),
      ),
    );
  }

  submitComment(): void {
    if (this.model.invalid) {
      return;
    }
    this.addComment.emit(this.model.value.comment);
    this.model.reset();
  }

  discardDraft(): void {
    this.model.reset();
    this.formVisible$.next(false);
  }

  setupMention() {
    return {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      mentionDenotationChars: ['@'],
      onSelect: (item, insertItem) => {
        const editor = this.quill.quillEditor;
        insertItem(item);
        // necessary because quill-mention triggers changes as 'api' instead of 'user'
        editor.insertText(editor.getLength() - 1, '', 'user');
      },
      source: async (searchTerm, renderList, mentionChar) => {
        searchTerm = searchTerm.toLocaleLowerCase();
        this.organizationService
          .getCurrentOrganization()
          .pipe(
            first(),
            switchMap((organizationId) =>
              this.getMembersGQL
                .fetch({ variables: { organizationId } })
                .pipe(pluck('data', 'getMembers')),
            ),
          )
          .subscribe((members) => {
            renderList(
              members
                .filter((member) => {
                  if (searchTerm.length === 0) {
                    return true;
                  }
                  return (
                    member.name.toLocaleLowerCase().indexOf(searchTerm) > -1
                  );
                })
                .map(({ name, userId }) => ({
                  id: userId,
                  value: name,
                })),
              searchTerm,
            );
          });
      },
    };
  }

  created(editor: Quill) {
    console.log('editor created');
    console.log(editor);
    this.editor = editor;

    editor.focus();
  }
}
